import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  extractErrorMessage,
  PriorityLevel,
  SubtaskDto,
  useSubtaskCreate,
  useSubtaskEdit,
} from '@sib/api';
import { useTranslation } from '@sib/i18n';
import { useNotification } from '@sib/notifications';
import { FormMode } from '@sib/utils';
import { z } from 'zod';

export enum SubtaskField {
  Completed = 'completed',
  Title = 'title',
  Description = 'description',
  PriorityLevel = 'priorityLevel',
}

const SUBTASK_TITLE_MAX_LENGTH = 64;
const SUBTASK_DESCRIPTION_MAX_LENGTH = 256;

export const useSubtaskValidationSchema = () => {
  const { t } = useTranslation();

  return useMemo(
    () =>
      z.object({
        [SubtaskField.Completed]: z.boolean(),
        [SubtaskField.Title]: z
          .string()
          .transform(value => value.trim())
          .pipe(
            z
              .string()
              .min(1, {
                message: t('errors.validation.required', {
                  fieldLabel: t('components.taskForm.fields.title.label'),
                }),
              })
              .max(SUBTASK_TITLE_MAX_LENGTH, {
                message: t('errors.validation.cantBeLonger', {
                  fieldLabel: t('components.taskForm.fields.title.label'),
                  maxLength: SUBTASK_TITLE_MAX_LENGTH,
                }),
              })
          ),
        [SubtaskField.Description]: z
          .string()
          .transform(value => value.trim())
          .pipe(
            z.string().max(SUBTASK_DESCRIPTION_MAX_LENGTH, {
              message: t('errors.validation.cantBeLonger', {
                fieldLabel: t('components.taskForm.fields.description.label'),
                maxLength: SUBTASK_DESCRIPTION_MAX_LENGTH,
              }),
            })
          ),
        [SubtaskField.PriorityLevel]: z.nativeEnum(PriorityLevel),
      }),
    [t]
  );
};

export type SubtaskFormValues = z.infer<
  ReturnType<typeof useSubtaskValidationSchema>
>;

export type SubtaskFormInputValues = z.input<
  ReturnType<typeof useSubtaskValidationSchema>
>;

const DEFAULT_VALUES: SubtaskFormInputValues = {
  [SubtaskField.Completed]: false,
  [SubtaskField.Title]: '',
  [SubtaskField.Description]: '',
  [SubtaskField.PriorityLevel]: PriorityLevel.Low,
};

const getDefaultValues = (subtask?: SubtaskDto): SubtaskFormInputValues =>
  subtask
    ? {
        [SubtaskField.Completed]: subtask.completed,
        [SubtaskField.Title]: subtask.title,
        [SubtaskField.Description]: subtask.description ?? '',
        [SubtaskField.PriorityLevel]:
          subtask.priorityLevel ?? PriorityLevel.Low,
      }
    : DEFAULT_VALUES;

interface UseSubtaskFormParams {
  taskId: string;
  /** Subtask to edit. When omitted, the form creates a new subtask. */
  subtask?: SubtaskDto;
  onGoBack: () => void;
}

export const useSubtaskForm = ({
  taskId,
  subtask,
  onGoBack,
}: UseSubtaskFormParams) => {
  const { t } = useTranslation();
  const { open } = useNotification();
  const validationSchema = useSubtaskValidationSchema();

  const formProps = useForm<SubtaskFormInputValues, null, SubtaskFormValues>({
    defaultValues: getDefaultValues(subtask),
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(validationSchema),
  });

  const { mutateAsync: createSubtask, isPending: isCreating } =
    useSubtaskCreate();
  const { mutateAsync: editSubtask, isPending: isEditing } = useSubtaskEdit();

  const formMode = subtask ? FormMode.Edit : FormMode.Create;

  const subtaskFormCallbacks = {
    [FormMode.Create]: {
      onSuccess: () => {
        open({
          type: 'success',
          message: t('notifications.create.success', {
            entity: t('entities.subtask'),
          }),
        });
        onGoBack();
      },
      onError: (error: unknown) => {
        open({
          type: 'error',
          message:
            extractErrorMessage(error) ||
            t('notifications.create.failure', {
              entity: t('entities.subtask'),
            }),
        });
      },
    },
    [FormMode.Edit]: {
      onSuccess: () => {
        open({
          type: 'success',
          message: t('notifications.edit.success', {
            entity: t('entities.subtask'),
          }),
        });
        onGoBack();
      },
      onError: (error: unknown) => {
        open({
          type: 'error',
          message:
            extractErrorMessage(error) ||
            t('notifications.edit.failure', {
              entity: t('entities.subtask'),
            }),
        });
      },
    },
  };

  const { onSuccess, onError } = subtaskFormCallbacks[formMode];

  const updateSubtask = async (
    subtaskId: string,
    values: SubtaskFormValues
  ) => {
    try {
      await editSubtask({
        taskUuid: taskId,
        uuid: subtaskId,
        title: values[SubtaskField.Title],
        description: values[SubtaskField.Description] || null,
        priorityLevel: values[SubtaskField.PriorityLevel],
        completed: values[SubtaskField.Completed],
      });

      onSuccess();
    } catch (error) {
      onError(error);
    }
  };

  const createSubtaskWithStatus = async (values: SubtaskFormValues) => {
    try {
      const created = await createSubtask({
        taskUuid: taskId,
        title: values[SubtaskField.Title],
        description: values[SubtaskField.Description] || null,
        priorityLevel: values[SubtaskField.PriorityLevel],
      });

      if (values[SubtaskField.Completed])
        await editSubtask({
          taskUuid: taskId,
          uuid: created.uuid,
          completed: true,
        });

      onSuccess();
    } catch (error) {
      onError(error);
    }
  };

  const onSubmit = (values: SubtaskFormValues) =>
    subtask
      ? updateSubtask(subtask.uuid, values)
      : createSubtaskWithStatus(values);

  return {
    formProps,
    onSubmit,
    isPending: isCreating || isEditing,
    isValid: formProps.formState.isValid,
    isDirty: formProps.formState.isDirty,
  };
};
